<template>
  <v-form ref="form" v-model="valid">
    <h3 class="title mb-4">Landlord Information</h3>
    
    <!-- Landlord existence check -->
    <v-radio-group
      v-model="landlordData.isExisting"
      row
      class="mb-4"
    >
      <template v-slot:label>
        <div class="text-subtitle-1 black--text">
          Does this landlord already exist in agentPay?
        </div>
      </template>
      <v-radio label="Yes" :value="true"></v-radio>
      <v-radio label="No" :value="false"></v-radio>
    </v-radio-group>
    
    <!-- Existing landlord search -->
    <div v-if="landlordData.isExisting === true" class="mb-4">
      <autocomplete-search
        v-model="landlordData.existingLandlord"
        :search-function="searchFunction"
        label="Search for existing landlord"
        placeholder="Enter at least 5 characters to search..."
        :rules="[rules.required]"
        @change="handleLandlordSelection"
      />
      
      <v-card v-if="landlordData.existingLandlord" class="mt-4" outlined>
        <v-card-text>
          <div class="text-subtitle-2 mb-2">Selected Landlord:</div>
          <div>{{ landlordData.existingLandlord.Subject }}</div>
          <div class="text-caption">ID: {{ landlordData.existingLandlord.OID }}</div>
        </v-card-text>
      </v-card>
    </div>
    
    <!-- New landlord form -->
    <div v-else-if="landlordData.isExisting === false">
      <v-row>
        <v-col cols="12" md="2">
          <v-select
            v-model="landlordData.newLandlord.title"
            :items="titles"
            label="Title"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="landlordData.newLandlord.forename"
            label="Forename"
            outlined
            dense
            :rules="[rules.required]"
            required
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            v-model="landlordData.newLandlord.middleName"
            label="Middle Name"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="landlordData.newLandlord.surname"
            label="Surname"
            outlined
            dense
            :rules="[rules.required]"
            required
          />
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="12" md="6">
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="landlordData.newLandlord.dateOfBirth"
                label="Date of Birth"
                prepend-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="landlordData.newLandlord.dateOfBirth"
              @input="dateMenu = false"
              :max="maxDate"
            />
          </v-menu>
        </v-col>
      </v-row>
      
      <v-divider class="my-4" />
      <h4 class="subtitle-1 mb-3">Address Details</h4>
      
      <v-row>
        <v-col cols="12">
          <v-text-field
            v-model="landlordData.newLandlord.address1"
            label="Address Line 1"
            outlined
            dense
            :rules="[rules.required]"
            required
          />
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="landlordData.newLandlord.address2"
            label="Address Line 2"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="landlordData.newLandlord.address3"
            label="Address Line 3"
            outlined
            dense
          />
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="landlordData.newLandlord.address4"
            label="Address Line 4"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="landlordData.newLandlord.postcode"
            label="Postcode"
            outlined
            dense
            :rules="[rules.required, rules.postcode]"
            required
          />
        </v-col>
      </v-row>
      
      <v-divider class="my-4" />
      <h4 class="subtitle-1 mb-3">Contact Details</h4>
      
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="landlordData.newLandlord.mobilePhone"
            label="Mobile Phone"
            outlined
            dense
            :rules="[rules.required, rules.phone]"
            required
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="landlordData.newLandlord.contactEmail"
            label="Contact Email"
            outlined
            dense
            :rules="[rules.required, rules.email]"
            required
          />
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="12">
          <v-checkbox
            v-model="landlordData.newLandlord.emailLetters"
            label="Send letters by email"
            hide-details
          />
        </v-col>
      </v-row>

      <!-- Management Fee Section (Optional) -->
      <v-divider class="my-4" />
      <h4 class="subtitle-1 mb-3">Management Fee (Optional)</h4>

      <v-row>
        <v-col cols="12">
          <p class="text-caption mb-2">
            Both percentage and effective date must be provided together to set a management fee
          </p>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model.number="landlordData.newLandlord.managementFeePercent"
            label="Management Fee (%)"
            type="number"
            step="0.01"
            min="0"
            max="100"
            suffix="%"
            outlined
            dense
            :rules="[rules.managementFeePercent]"
            hint="Percentage management fee to charge (0-100)"
            persistent-hint
          ></v-text-field>
        </v-col>

        <v-col cols="12" md="6">
          <v-menu
            v-model="managementFeeDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="landlordData.newLandlord.dateManagementFee"
                label="Effective From"
                prepend-icon="mdi-calendar"
                readonly
                outlined
                dense
                :rules="[rules.dateManagementFee]"
                hint="Date from which this fee applies"
                persistent-hint
                clearable
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="landlordData.newLandlord.dateManagementFee"
              :min="todayDate"
              @input="managementFeeDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
    </div>
    
    <!-- Navigation buttons -->
    <v-row class="mt-6">
      <v-col cols="6">
        <v-btn
          text
          @click="$emit('back')"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </v-col>
      <v-col cols="6" class="text-right">
        <v-btn
          color="primary"
          :disabled="!canProceed"
          @click="proceed"
        >
          Next
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import AutocompleteSearch from '@/components/Tenancies/AutocompleteSearch.vue'

export default {
  name: 'LandlordStep',
  
  components: {
    AutocompleteSearch
  },
  
  props: {
    value: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    searchFunction: {
      type: Function,
      required: true
    }
  },
  
  data() {
    return {
      valid: false,
      dateMenu: false,
      managementFeeDateMenu: false,
      landlordData: this.value,
      titles: ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Prof', 'Sir', 'Lady'],
      rules: {
        required: v => !!v || 'This field is required',
        email: v => {
          if (!v) return true
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          return emailRegex.test(v) || 'Please enter a valid email address'
        },
        phone: v => {
          if (!v) return true
          const phoneRegex = /^[\d\s\-+()]+$/
          return phoneRegex.test(v) || 'Please enter a valid phone number'
        },
        postcode: v => {
          if (!v) return true
          const postcodeRegex = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i
          return postcodeRegex.test(v) || 'Please enter a valid UK postcode'
        },
        managementFeePercent: v => {
          // Optional - but if dateManagementFee has value, this becomes required
          if (this.landlordData.newLandlord.dateManagementFee && !v) {
            return 'Management fee percentage is required when date is provided'
          }
          // If provided, validate range
          if (v !== null && v !== '') {
            if (v < 0 || v > 100) {
              return 'Management fee must be between 0 and 100'
            }
          }
          return true
        },
        dateManagementFee: v => {
          // Optional - but if managementFeePercent has value, this becomes required
          if ((this.landlordData.newLandlord.managementFeePercent !== null &&
               this.landlordData.newLandlord.managementFeePercent !== '') && !v) {
            return 'Management fee date is required when percentage is provided'
          }
          // If provided, validate that it's today or in the future
          if (v) {
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            const selectedDate = new Date(v)
            if (selectedDate < today) {
              return 'Management fee date must be today or in the future'
            }
          }
          return true
        }
      }
    }
  },
  
  computed: {
    maxDate() {
      return new Date().toISOString().substr(0, 10)
    },

    todayDate() {
      return new Date().toISOString().substr(0, 10)
    },

    canProceed() {
      if (this.landlordData.isExisting === null) {
        return false
      }
      
      if (this.landlordData.isExisting) {
        return !!this.landlordData.existingLandlord
      } else {
        return this.valid && 
               this.landlordData.newLandlord.forename && 
               this.landlordData.newLandlord.surname &&
               this.landlordData.newLandlord.address1 &&
               this.landlordData.newLandlord.postcode &&
               this.landlordData.newLandlord.mobilePhone &&
               this.landlordData.newLandlord.contactEmail
      }
    }
  },
  
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.landlordData = newVal
      }
    },
    landlordData: {
      deep: true,
      handler(val) {
        this.$emit('input', val)
      }
    }
  },
  
  methods: {
    handleLandlordSelection() {
      // Handle landlord selection if needed
    },
    
    proceed() {
      if (this.canProceed) {
        this.$emit('next')
      }
    }
  }
}
</script>